import cx from 'classnames';
import React from 'react';
import { BaseSelector, BaseSelectorProps, SwatchShapeOptions } from '../base/base-selector';
import styles from './color.module.scss';

export enum BorderStyle {
  SOLID = 'solid',
}

export interface BorderProps {
  width: number;
  style: BorderStyle;
  color: string;
}

interface BaseSelectorStyleProps {
  border?: string;
}

export interface Props extends BaseSelectorProps {
  /**
   * default selected option
   * @default ''
   */
  selectedOption?: string;

  /**
   * Adds border around the swatch shape
   * @default false
   */
  showBorder?: boolean;

  /** a11y prop */
  tabIndex?: number;

  /**
   * Customize the border style to override the default color selector border.
   * This is used in the chart legend when series points are hovered over or selected,
   * changing the legend color selector border to the primary blue color.
   *
   * @default undefined
   */
  border?: BorderProps;
}

type ColorProps = Omit<Props, 'children'>;

export const Color = ({
  selectedOption = '',
  swatchShape,
  showBorder = false,
  tabIndex = 0,
  border = undefined,
  ...restProps
}: ColorProps) => {
  const selectValueClass = cx(styles.selectedValue, styles.selectedColor, {
    [styles.swatchRound]: swatchShape === SwatchShapeOptions.ROUND,
    [styles.swatchRoundLarge]: swatchShape === SwatchShapeOptions.ROUND_LARGE,
    [styles.border]: showBorder,
    [styles.selectedEmptyColor]: selectedOption === '',
  });

  const baseSelectorStyles: BaseSelectorStyleProps = {};
  if (border) {
    baseSelectorStyles.border = `${border.width}px ${border.style} ${border.color}`;
  }

  return (
    <>
      <BaseSelector swatchShape={swatchShape} {...restProps} tabIndex={tabIndex} baseSelectorClass={styles.colorPicker}>
        <div
          className={`${selectValueClass} ${styles.colorPickerPreview}`}
          style={{
            backgroundColor: selectedOption,
            ...baseSelectorStyles,
          }}
          data-testid="base-selector-div-testid"
        />
      </BaseSelector>
    </>
  );
};
